import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { SubtestScores, CompositeScores, ScoreInterpretation } from '../types';
import { SUBTEST_SCORE_MAX, COMPOSITE_SCORE_MAX } from '../utils/scoreUtils';

/**
 * Input guard for score fields. Accepts:
 * - '' (clearing the field),
 * - plain digit strings whose numeric value does not exceed the range max.
 * Values below the minimum are allowed transiently because they can be
 * prefixes of a valid score while typing (e.g. "1" -> "11" -> "115").
 * Rejects negative, non-numeric and above-max values (e.g. "-50", "abc", "999").
 */
const isStorableScoreInput = (value: string, max: number): boolean => {
  if (value === '') return true;
  if (!/^\d+$/.test(value)) return false;
  return parseInt(value, 10) <= max;
};

const filterStorableScores = <T extends Record<string, string | undefined>>(
  scores: Partial<T>,
  max: number
): Partial<T> => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(scores)) {
    if (typeof value === 'string' && isStorableScoreInput(value, max)) {
      result[key] = value;
    }
  }
  return result as Partial<T>;
};

interface ScoresState {
  subtestScores: SubtestScores;
  compositeScores: CompositeScores;
  setSubtestScore: (key: keyof SubtestScores, value: string) => void;
  setCompositeScore: (key: keyof CompositeScores, value: string) => void;
  setAllSubtestScores: (scores: Partial<SubtestScores>) => void;
  setAllCompositeScores: (scores: Partial<CompositeScores>) => void;
  loadTestData: () => void;
  resetScores: () => void;
  getScoreInterpretation: (score: number, type: 'composite' | 'subtest') => ScoreInterpretation;
  getScoreColor: (key: keyof CompositeScores) => string;
}

const defaultSubtestScores: SubtestScores = {
  block_design: '',
  similarities: '',
  matrix_reasoning: '',
  digit_span: '',
  coding: '',
  vocabulary: '',
  figure_weights: '',
  visual_puzzles: '',
  picture_span: '',
  symbol_search: '',
};

const defaultCompositeScores: CompositeScores = {
  vci: '',
  vsi: '',
  fri: '',
  wmi: '',
  psi: '',
  fsiq: '',
};

// Test data for development
const testSubtestScores: SubtestScores = {
  block_design: '12',
  similarities: '14',
  matrix_reasoning: '11',
  digit_span: '10',
  coding: '8',
  vocabulary: '13',
  figure_weights: '12',
  visual_puzzles: '11',
  picture_span: '9',
  symbol_search: '7',
};

const testCompositeScores: CompositeScores = {
  vci: '118',
  vsi: '108',
  fri: '112',
  wmi: '97',
  psi: '85',
  fsiq: '106',
};

// Color mapping for cognitive domains
const compositeColors: Record<keyof CompositeScores, string> = {
  vci: '#3B82F6', // Blue - Verbal Comprehension
  vsi: '#10B981', // Green - Visual Spatial
  fri: '#8B5CF6', // Purple - Fluid Reasoning
  wmi: '#F59E0B', // Orange - Working Memory
  psi: '#EF4444', // Red - Processing Speed
  fsiq: '#6B7280', // Gray - Full Scale IQ
};

export const useScoresStore = create<ScoresState>()(
  persist(
    (set) => ({
      subtestScores: { ...defaultSubtestScores },
      compositeScores: { ...defaultCompositeScores },

      setSubtestScore: (key, value) =>
        set((state) => {
          if (!isStorableScoreInput(value, SUBTEST_SCORE_MAX)) return state;
          return { subtestScores: { ...state.subtestScores, [key]: value } };
        }),

      setCompositeScore: (key, value) =>
        set((state) => {
          if (!isStorableScoreInput(value, COMPOSITE_SCORE_MAX)) return state;
          return { compositeScores: { ...state.compositeScores, [key]: value } };
        }),

      setAllSubtestScores: (scores) =>
        set((state) => ({
          subtestScores: {
            ...state.subtestScores,
            ...filterStorableScores(scores, SUBTEST_SCORE_MAX),
          },
        })),

      setAllCompositeScores: (scores) =>
        set((state) => ({
          compositeScores: {
            ...state.compositeScores,
            ...filterStorableScores(scores, COMPOSITE_SCORE_MAX),
          },
        })),

      loadTestData: () =>
        set({
          subtestScores: { ...testSubtestScores },
          compositeScores: { ...testCompositeScores },
        }),

      resetScores: () =>
        set({
          subtestScores: { ...defaultSubtestScores },
          compositeScores: { ...defaultCompositeScores },
        }),

      getScoreInterpretation: (score: number, type: 'composite' | 'subtest'): ScoreInterpretation => {
        if (type === 'composite') {
          if (score >= 130) return 'Bardzo wysoki';
          if (score >= 120) return 'Wysoki';
          if (score >= 110) return 'Powyżej przeciętnej';
          if (score >= 90) return 'Przeciętny';
          if (score >= 80) return 'Poniżej przeciętnej';
          if (score >= 70) return 'Niski';
          return 'Bardzo niski';
        } else {
          // Subtest scores (1-19)
          if (score >= 16) return 'Bardzo wysoki';
          if (score >= 14) return 'Wysoki';
          if (score >= 12) return 'Powyżej przeciętnej';
          if (score >= 8) return 'Przeciętny';
          if (score >= 6) return 'Poniżej przeciętnej';
          if (score >= 4) return 'Niski';
          return 'Bardzo niski';
        }
      },

      getScoreColor: (key) => compositeColors[key],
    }),
    {
      name: 'wiscv-scores',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
